import React from 'react';

import { Box, Stack, Typography } from '@mui/material';

import { SeatMapRow, SeatSelectionState, Traveller } from '../charter.types';
import { getFacilityLayoutAttributes } from '../common/getFacilityLayoutAttributes';

import FacilityTile from './FacilityTile';
import SeatTile from './SeatTile';
import { BUSINESS_SEAT_WIDTH, ECONOMY_SEAT_SIZE, SEAT_HEIGHT } from './seatConstants';

type SeatRowProps = {
  row: SeatMapRow;
  flightId: string;
  seatSelections: SeatSelectionState;
  travellers: Array<Traveller>;
};

export default function SeatRow({ row, flightId, seatSelections, travellers }: SeatRowProps): React.JSX.Element {
  const layout = row.layout;
  const isFacilityRow = row.isFacilityRow ?? false;
  const seatWidth = row.cabin === 'business' ? BUSINESS_SEAT_WIDTH : ECONOMY_SEAT_SIZE;
  const aisleWidth = row.cabin === 'business' ? 18 : 12;
  const facilityLayoutAttributes = new Map<number, ReturnType<typeof getFacilityLayoutAttributes>>();

  const gridTemplateColumns = layout
    .flatMap((segment, index) => {
      if (segment.kind === 'aisle') {
        return [`${aisleWidth}px`];
      }

      if (segment.kind === 'facility') {
        const attributes = getFacilityLayoutAttributes(segment);
        facilityLayoutAttributes.set(index, attributes);

        return Array.from({ length: attributes.columnSpan }, () => `${seatWidth}px`);
      }

      return [`${seatWidth}px`];
    })
    .join(' ');

  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
      <Typography
        variant="caption"
        sx={{ width: 28, textAlign: 'right', fontWeight: 600, opacity: isFacilityRow ? 0 : 1, flexShrink: 0 }}
      >
        {isFacilityRow ? '' : row.rowNumber}
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns,
          gap: 0.75,
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
          px: 1.5,
        }}
      >
        {layout.map((segment, index) => {
          if (segment.kind === 'aisle') {
            return (
              <Box
                key={`${row.rowNumber}-${segment.id}-${index}`}
                sx={{
                  width: '100%',
                  height: SEAT_HEIGHT,
                }}
              />
            );
          }

          if (segment.kind === 'facility') {
            return (
              <FacilityTile
                key={`${row.rowId}-facility-${index}`}
                facility={segment}
                attributes={facilityLayoutAttributes.get(index)}
              />
            );
          }

          return (
            <SeatTile
              key={`${row.rowId}-${segment.code}`}
              seatId={`${row.rowNumber}${segment.code}`}
              row={row}
              flightId={flightId}
              seatSelections={seatSelections}
              travellers={travellers}
            />
          );
        })}
      </Box>
    </Stack>
  );
}
